import { ErrorSidePanel } from '@/components/errors';
import { formatPhone } from '@/lib';
import { ImageProfile, mage, templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useGetUsers } from '../-hooks/get-users.hook';
import { useDeleteUser } from '../-hooks/delete-user.hook';
import { useResendInvite } from '../-hooks/resend-invite.hook';
import { useGetUserApplications, useToggleAppPermission } from '../-hooks/user-applications.hook';
import { type UserDetailProps } from './-model/props';
import { UserDetailLight } from './-skins';

export const Route = createFileRoute('/(services)/services/_sheet/users/$userId/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const { data: users } = useGetUsers();
  const user = users?.find((u) => u.Id === userId);
  const { data: applications, isLoading: appsLoading } = useGetUserApplications(userId);
  const { mutateAsync: toggleApp } = useToggleAppPermission();
  const { mutateAsync: deleteUser, isPending: deleteLoading } = useDeleteUser();
  const { mutateAsync: resendInvite, isPending: resendLoading } = useResendInvite();
  const [togglingAppId, setTogglingAppId] = useState<string | null>(null);

  async function handleToggleApp(appId: string, isEnabled: boolean) {
    setTogglingAppId(appId);
    try {
      await toggleApp({ appId, userId, isEnabled });
    } finally {
      setTogglingAppId(null);
    }
  }

  async function handleDeleteUser() {
    await deleteUser(userId);
    navigate({ to: '/services/users' });
  }

  async function handleResendInvite() {
    await resendInvite(userId);
  }

  const email = user?.UsernameAliases?.find((a) => a.Type === 'Email')?.Username;
  const phone = user?.UsernameAliases?.find((a) => a.Type === 'Mobile')?.Username;
  const pictureUri = user?.PamPictureUri
    ? mage.getImage(user.PamPictureUri, ImageProfile.App)
    : null;

  const props: UserDetailProps = {
    userId,
    displayName: user?.DisplayName ?? '',
    email,
    phone: phone ? formatPhone(phone) : undefined,
    pictureUri,
    isPending: user?.State === 'Pending',
    applications: applications ?? [],
    appsLoading,
    togglingAppId,
    deleteLoading,
    resendLoading,
    onToggleApp: (id, enabled) => void handleToggleApp(id, enabled),
    onResendInvite: () => {
      handleResendInvite().catch(() => {
        // Prevent unhandled promise rejections when the callback contract is synchronous.
      });
    },
    onDeleteUser: handleDeleteUser,
  };
  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: UserDetailProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <UserDetailLight {...props} />,
  };
}
